import React from 'react';
import { string, bool, object, func } from 'prop-types';
import theme from '@airasia-common/libraries/theming/theme';
import styled from 'styled-components';
import PriceText, { Variant } from '../Text/PriceText';

const hoverStyle = ({ active, priceNotAvailable }) => {
  if (priceNotAvailable) {
    return !active ? theme.colors.grey : theme.colors.greyDarker;
  }
  return !active ? theme.colors.tertiaryBlueLighter : theme.colors.secondary;
};

const activeStyle = ({ active, priceNotAvailable }) => {
  if (priceNotAvailable) {
    return !active ? theme.colors.greyLight : theme.colors.greyDarker;
  }
  return !active ? theme.colors.primaryWhite : theme.colors.secondary;
};

const HotelMarkerWrapper = styled.div`
  height: ${props => props.height || '30px'};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: ${props => props.top || '0'};
  transform: translate(
    ${props => props.translateX || '-50%'},
    ${props => props.translateY || '-50%'}
  );

  &:hover .hotel_marker_down_base_inner {
    border-top-color: ${props => hoverStyle(props)};
  }
`;

const HotelMarkerContentdiv = styled.div`
  text-align: center;
  position: relative;
  min-width: 69px;
  min-height: 22px;
  justify-content: center;
  align-items: center;
  padding: 6px 4px;
  display: flex;
  background-color: ${props => activeStyle(props)};
  border: 1px solid
    ${props =>
      props.priceNotAvailable
        ? theme.colors.greyDarker
        : theme.colors.secondary};
  border-radius: 4px;
  z-index: ${props => (props.active ? 5 : 2)};

  &:hover {
    background-color: ${props => hoverStyle(props)};
  }
`;

const MarkerWrapper = styled.div`
  width: 0;
  height: 0;
  position: relative;
`;

const HotelMarkerArrowDownBaseInner = styled(MarkerWrapper)`
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 10px solid transparent;
  z-index: ${props => (props.active ? 2 : 5)};
  top: -3px;
  border-top-color: ${props => activeStyle(props)};
`;

const HotelMarkerArrowDownBase = styled(MarkerWrapper)`
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 12px solid transparent;
  z-index: 1;
  top: -13px;
  border-top-color: ${props =>
    props.priceNotAvailable ? theme.colors.greyDarker : theme.colors.secondary};
`;

const HotelMapMarker = props => {
  const {
    active,
    currency,
    rateValue,
    styles,
    onClick,
    noPriceAvailableLabel,
  } = props;
  // rateValue is parsed as a string because in some scenarios,
  // rateValue can be equal to "1m", "1k", or other shorthand notations.
  const isRateValueIsValidNumber = /\d/.test(rateValue);
  const label = isRateValueIsValidNumber ? rateValue : noPriceAvailableLabel;

  return (
    <HotelMarkerWrapper
      priceNotAvailable={!isRateValueIsValidNumber}
      active={active}
      {...styles}
      onClick={onClick}
    >
      <HotelMarkerContentdiv
        priceNotAvailable={!isRateValueIsValidNumber}
        active={active}
      >
        <PriceText
          variant={
            active
              ? Variant.MarkerPriceActive
              : !isRateValueIsValidNumber
              ? Variant.NoAvailableHotelInactive
              : Variant.MarkerPriceSecondaryInactive
          }
          margin={0}
          currency={isRateValueIsValidNumber ? currency : ''}
        >
          {label}
        </PriceText>
      </HotelMarkerContentdiv>
      <HotelMarkerArrowDownBaseInner
        className="hotel_marker_down_base_inner"
        priceNotAvailable={!isRateValueIsValidNumber}
        active={active}
      />
      <HotelMarkerArrowDownBase
        priceNotAvailable={!isRateValueIsValidNumber}
        active={active}
      />
    </HotelMarkerWrapper>
  );
};

HotelMapMarker.propTypes = {
  currency: string,
  rateValue: string,
  active: bool,
  styles: object,
  onClick: func,
  noPriceAvailableLabel: string,
};

HotelMapMarker.defaultProps = {
  currency: '',
  rateValue: '',
  active: false,
  styles: {},
  onClick: () => {},
  noPriceAvailableLabel: 'Sold out', // default to english, based on the figma design
};

export default HotelMapMarker;
